import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { LoginCredentials } from '@/types/auth.types';
import PublicRoute from '@/routes/PublicRoute';
import logo from '@/assets/core-brain-symbol-320.jpg';   // 320 px copy (46 kB) of the 954 kB original
import { Shield, User, Lock, Eye, EyeOff, AlertTriangle, LogIn } from 'lucide-react';
import './Login.css';

export default function LoginPage() {
  const { login, loading, error } = useAuth();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    identifier: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [clock, setClock] = useState('');

  useEffect(() => {
    const tick = () =>
      setClock(new Date().toLocaleTimeString('en-US', { hour12: false }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(credentials);
  };

  return (
    <PublicRoute>
      <div className="login-screen">
        {/* ═══ LEFT — BRAND PANEL ═══ */}
        <div className="login-brand-panel">
          {/* Corner brackets */}
          <div className="login-corner login-corner--tl" />
          <div className="login-corner login-corner--tr" />
          <div className="login-corner login-corner--bl" />
          <div className="login-corner login-corner--br" />

          <div className="login-brand-content">
            <div className="login-logo-glow">
              <img src={logo} alt="AUREXIS" />
            </div>
            <h1 className="login-brand-title">AUREXIS</h1>
            <p className="login-brand-subtitle">
              Autonomous Intelligence Command System
            </p>
            <p className="login-brand-tagline">
              Cognitive Trading Platform &middot; v2.0
            </p>

            <div className="login-status-bar">
              <div className="login-status-item">
                <span className="login-status-dot" />
                Systems Online
              </div>
              <div className="login-status-item">
                <span className="login-status-dot login-status-dot--cyan" />
                Neural Active
              </div>
              <div className="login-status-item">
                <span className="login-status-dot login-status-dot--amber" />
                Markets Live
              </div>
            </div>
          </div>

          <div className="login-classification">
            <div className="login-classification-line" />
            <span className="login-classification-text">
              Clearance Level Required
            </span>
            <div className="login-classification-line" />
          </div>
        </div>

        {/* ═══ RIGHT — AUTH PANEL ═══ */}
        <div className="login-auth-panel">
          {/* Secure bar */}
          <div className="login-secure-bar">
            <div className="login-secure-label">
              <span className="login-secure-dot" />
              Encrypted Channel Active
            </div>
            <span className="login-secure-clock">{clock}</span>
          </div>

          {/* Auth card */}
          <div className="login-auth-card">
            <div className="login-auth-header">
              <h2 className="login-auth-title">Operator Authentication</h2>
              <p className="login-auth-desc">
                Enter your credentials to access the AUREXIS command center.
                All sessions are encrypted and audited.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email field */}
              <div className="login-field">
                <label className="login-label" htmlFor="login-email">
                  Identifier / Email
                </label>
                <div className="login-input-wrap">
                  <input
                    id="login-email"
                    className="login-input"
                    type="text"
                    value={credentials.identifier}
                    onChange={(e) =>
                      setCredentials({ ...credentials, identifier: e.target.value })
                    }
                    placeholder="operator@aurexis.com"
                    required
                    autoFocus
                    autoComplete="username"
                  />
                  <span className="login-input-icon">
                    <User size={16} />
                  </span>
                </div>
              </div>

              {/* Password field */}
              <div className="login-field">
                <label className="login-label" htmlFor="login-password">
                  Access Key
                </label>
                <div className="login-input-wrap">
                  <input
                    id="login-password"
                    className="login-input"
                    type={showPassword ? 'text' : 'password'}
                    value={credentials.password}
                    onChange={(e) =>
                      setCredentials({ ...credentials, password: e.target.value })
                    }
                    placeholder="Enter your access key"
                    required
                    autoComplete="current-password"
                  />
                  <span className="login-input-icon">
                    <Lock size={16} />
                  </span>
                  <button
                    type="button"
                    className="login-pw-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Options row */}
              <div className="login-options">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember session
                </label>
                <button type="button" className="login-forgot">
                  Forgot access key?
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="login-error">
                  <AlertTriangle size={14} />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit */}
              <button type="submit" disabled={loading} className="login-submit">
                {loading ? (
                  <>
                    <span className="login-spinner" />
                    Authenticating
                  </>
                ) : (
                  <>
                    <Shield size={16} />
                    Authenticate
                  </>
                )}
              </button>
            </form>

            {/* Subtle warning */}
            <div
              style={{
                marginTop: '1.75rem',
                textAlign: 'center',
                fontSize: '0.625rem',
                color: 'var(--mil-text-muted)',
                lineHeight: 1.7,
                letterSpacing: '0.03em',
              }}
            >
              Unauthorized access is prohibited and monitored.
              <br />
              All sessions are logged, encrypted, and audited.
            </div>
          </div>

          {/* Footer */}
          <div className="login-footer">
            <span className="login-footer-text">
              &copy; 2026 AUREXIS &middot; Meaning X Corp
            </span>
            <div className="login-footer-crypto">
              <span>TLS 1.3</span>
              <span>AES-256</span>
              <span>End-to-End Encrypted</span>
            </div>
          </div>
        </div>
      </div>
    </PublicRoute>
  );
}